<script>
import { slide } from 'svelte/transition';
import { formatDistance, isSameDay, format } from 'date-fns';
import { second, minute } from '../data/datetime';
import { storedSprints, Status, StatusText, getStatus } from '../data/sprints';
import { router } from '../libs/router';
import { sanitizeText } from '../editor';
import { writable } from '../libs/store';
import { user } from '../data/user';
import { createSprintTotalsStore } from '../data/server';
import { readable } from '../libs/store';
import { tooltipTop } from '../tooltip';
import globals from '../globals';
import { analytics } from '../data/firebase';

export let sprint;
const sprintStore = writable(sprint);
const sprintTotals = createSprintTotalsStore(sprintStore, minute);

const TimePrefix = {
  [Status.Pending]: 'starts in',
  [Status.Open]: 'starts in',
  [Status.Started]: 'finishing',
  [Status.Finished]: 'finished',
}

$: $sprintStore = sprint;
$: duration = ((sprint.end - sprint.start) / 1000 / 60) || 0;
$: isGroup = sprint.id.length === 12;
$: type = isGroup ? 'Group' : 'Personal';
$: listed = sprint.listed ? 'Listed ' : '';
$: unit = duration < 60 ? 'Minute' : 'Hour';
$: value = duration < 60 ? duration : duration / 60;
$: title = sprint.title ? sanitizeText(sprint.title) : `${value} ${unit} ${type} Sprint`;
$: status = getStatus(sprint, $second);
$: statusText = StatusText[status];
$: timePrefix = TimePrefix[status];
$: time = sprint.start <= $second ? sprint.end : sprint.start;
$: hasStarted = status === Status.Started || status === Status.Finished;
$: hasOpened = status !== Status.Pending;
$: participants = isGroup ? $sprintTotals.participants || 0 : 1;
$: wordcount = isGroup ? $sprintTotals.total || 0 : sprint.wordcount || 0;
$: editable = status === Status.Pending || status === Status.Open;

function copyLink(event) {
  event.stopPropagation();
  event.preventDefault();
  const url = event.target.closest('a').href;
  const input = document.createElement('input');
  document.body.appendChild(input);
  input.value = url;
  input.select();
  document.execCommand('copy');
  input.remove();
  globals.inform('success', 'Link Copied', `to your clipboard.`);
  analytics.logEvent('copied_link');
}

function editSprint() {
  sessionStorage.lastUrl = location.pathname;
  router.navigate(`/sprint-edit/${sprint.id}`);
}
</script>

<div class="sprint-item {status}" on:click={event => event.target.tagName !== 'A' && router.navigate(`/sprints/${sprint.id}`)} transition:slide|local={{ duration: 200 }}>
  <div class="top">
    <h5 class="title"><a href="/sprints/{sprint.id}">{title}</a></h5>
    {#if $user && sprint.creator === $user.uid && editable}
    <button class="btn xs" on:click={editSprint}>Edit</button>
    {/if}
    <div class="listing" class:listed={sprint.listed}>{sprint.listed ? 'Listed ' : 'Private '}</div>
    <div class="status">{statusText}</div>
  </div>
  <div class="bottom">
    <div class="meta">
      <span class="meta-item" use:tooltipTop={'Total Word Count'}>
        <i class="icon icon-word-sprint"></i>
        {wordcount}
      </span>
      {#if isGroup}
      <span class="meta-item" use:tooltipTop={'Participants'}>
        <i class="icon icon-account-multiple"></i>
        {participants}
      </span>
      <span class="meta-item sprint-link">
        <a href="/sprints/{sprint.id}" on:click={copyLink} use:tooltipTop={'Copy Link'}>
          <i class="icon icon-link"></i>
        </a>
      </span>
      {/if}
      <span class="meta-item" use:tooltipTop={'Duration'}>
        <i class="icon icon-timer-outline"></i>
        {`${value} ${unit.toLowerCase()}${value === 1 ? '' : 's'}`}
      </span>
    </div>

    <div class="sprint-time" use:tooltipTop={format(new Date(sprint.start), 'PPP') + '<br><highlight>' + format(new Date(sprint.start), 'p') + '</highlight>'}>
      {timePrefix}
      <strong>
        {formatDistance(new Date(time), $second, { addSuffix: true })}
      </strong>
    </div>
  </div>
</div>

<style>
.sprint-item {
  margin: 12px 0;
  padding: 12px;
  background: var(--white);
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .15);
  cursor: pointer;
  /* transition: all .15s ease-in-out; */
}
.sprint-item:hover {
  transform: translate(0, -1px);
  box-shadow: 0 1px 3px rgba(0, 0, 0, .1), 0 3px 8px rgba(0, 0, 0, .1);
}
.top, .bottom {
  display: flex;
  align-items: baseline;
}
.bottom {
  margin-top: 12px;
}
.sprint-item.finished {
  opacity: .7;
}
.title {
  flex: 1;
  margin: 0;
  font-size: var(--font-size-400);
}
.title a {
  color: var(--cyan-600);
}
.sprint-time {
  font-size: var(--font-size-100);
  color: var(--warm-grey-500);
}
.status, .listing {
  padding: 4px 12px;
  font-size: var(--font-size-050);
  color: var(--warm-grey-800);
  background: var(--warm-grey-200);
  border-radius: 20px;
  white-space: nowrap;
  margin-left: 4px;
}
.listing.listed {
  background: var(--teal-500);
  color: var(--white);
}
.sprint-item.started .status, .sprint-item.open .status {
  background: var(--blue-400);
  color: var(--white);
}
.sprint-item.pending .status {
  background: var(--blue-050);
}
.meta {
  flex: 1;
  font-size: var(--font-size-100);
  color: var(--warm-grey-500);
}
.meta-item {
  margin-right: 12px;
}
.meta-item .icon {
  position: relative;
  top: 2px;
}
.sprint-link a {
  color: inherit;
  text-decoration: none;
}
/* .meta-item.sprint-link .icon {
  position: relative;
  top: 6px;
  font-size: 24px;
  line-height: inherit;
} */
</style>
